import assetStatusService from "../../../../services/assetService/assetStatusService";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CACHE_KEY_ASSETSTATUSES } from "../../../../utils/constants";
import { AssetStatusInterface } from "../../../../models/AssetModel/AssetStatus";

interface DeleteAssetStatusContext {
  previousAssetStatuses: AssetStatusInterface[];
  deletedAssetStatus: AssetStatusInterface | undefined;
}

const useDeleteAssetStatus = (page: number, pageSize: number) => {
  const queryClient = useQueryClient();

  return useMutation<
    void, // No return data after deletion
    Error,
    string, // ID of the assetStatus to delete
    DeleteAssetStatusContext
  >({
    mutationFn: assetStatusService.Remove, // Remove should take a string ID

    onMutate: (assetStatusId: string) => {
      const previousAssetStatuses =
        queryClient.getQueryData<AssetStatusInterface[]>([
          CACHE_KEY_ASSETSTATUSES,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ]) || [];

      // Optimistically update the cache
      queryClient.setQueryData<AssetStatusInterface[]>(
        [
          CACHE_KEY_ASSETSTATUSES,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ],
        previousAssetStatuses.filter(
          (assetStatus) => assetStatus.id !== assetStatusId
        ) // Use assetStatusId to filter
      );

      return {
        previousAssetStatuses,
        deletedAssetStatus: previousAssetStatuses.find(
          (assetStatus) => assetStatus.id === assetStatusId
        ),
      };
    },

    onError: (error, assetStatusId, context) => {
      if (!context) return;

      // Rollback to the previous state in case of error
      queryClient.setQueryData<AssetStatusInterface[]>(
        [
          CACHE_KEY_ASSETSTATUSES,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ],
        context.previousAssetStatuses
      );
    },

    onSuccess: () => {
      // Optionally, you could perform additional actions on success
      // For example, invalidating queries or updating other parts of the state
    },
  });
};

export default useDeleteAssetStatus;
